import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { Progress } from "../components/ui/progress";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  Activity,
  RefreshCw,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Clock,
  Wifi,
  WifiOff,
  Zap,
  TrendingUp,
  Server,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";

const STATUS_CONFIG = {
  healthy: { color: "bg-green-500", textColor: "text-green-600", icon: CheckCircle, label: "Healthy" },
  degraded: { color: "bg-yellow-500", textColor: "text-yellow-600", icon: AlertTriangle, label: "Degraded" },
  timeout: { color: "bg-orange-500", textColor: "text-orange-600", icon: Clock, label: "Timeout" },
  unreachable: { color: "bg-red-500", textColor: "text-red-600", icon: WifiOff, label: "Unreachable" },
  error: { color: "bg-red-500", textColor: "text-red-600", icon: XCircle, label: "Error" },
  no_endpoint: { color: "bg-gray-500", textColor: "text-gray-600", icon: Server, label: "No Endpoint" },
  unknown: { color: "bg-gray-400", textColor: "text-gray-500", icon: Activity, label: "Unknown" },
};

export default function DSPHealthPage() {
  const { api } = useAuth();
  const [healthData, setHealthData] = useState(null);
  const [historyData, setHistoryData] = useState(null);
  const [latencyData, setLatencyData] = useState([]);
  const [bidRateData, setBidRateData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPeriod, setSelectedPeriod] = useState("24");
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchHealthData = useCallback(async (showToast = false) => {
    try {
      setRefreshing(true);
      const [healthRes, historyRes, latencyRes, bidRateRes] = await Promise.all([
        api.get("/dsp-health/check-all"),
        api.get(`/dsp-health/history?hours=${selectedPeriod}`),
        api.get(`/dsp-health/latency-timeseries?hours=${selectedPeriod}`),
        api.get("/dsp-health/bid-rates"),
      ]);
      
      setHealthData(healthRes.data);
      setHistoryData(historyRes.data);
      setBidRateData(bidRateRes.data);
      
      // Process latency data for chart
      const processedLatency = processLatencyData(latencyRes.data.data);
      setLatencyData(processedLatency);
      
      if (showToast) {
        toast.success("Health check completed");
      }
    } catch (error) {
      toast.error("Failed to fetch health data");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [api, selectedPeriod]);

  // Process latency data for chart - group by time intervals
  const processLatencyData = (data) => {
    if (!data || data.length === 0) return [];
    
    // Group by 30-minute intervals
    const grouped = {};
    data.forEach(item => {
      const date = new Date(item.timestamp);
      const key = new Date(Math.floor(date.getTime() / (30 * 60 * 1000)) * (30 * 60 * 1000)).toISOString();
      
      if (!grouped[key]) {
        grouped[key] = { timestamp: key, latencies: [], dsps: {} };
      }
      
      grouped[key].latencies.push(item.latency_ms);
      if (!grouped[key].dsps[item.dsp_name]) {
        grouped[key].dsps[item.dsp_name] = [];
      }
      grouped[key].dsps[item.dsp_name].push(item.latency_ms);
    });
    
    // Calculate averages
    return Object.values(grouped).map(g => {
      const result = {
        time: new Date(g.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
        avgLatency: Math.round(g.latencies.reduce((a, b) => a + b, 0) / g.latencies.length),
      };
      
      // Add per-DSP averages
      Object.entries(g.dsps).forEach(([name, latencies]) => {
        result[name] = Math.round(latencies.reduce((a, b) => a + b, 0) / latencies.length);
      });
      
      return result;
    }).slice(-24); // Last 24 data points
  };

  useEffect(() => {
    fetchHealthData();
  }, [fetchHealthData]);

  // Auto-refresh every 30 seconds if enabled
  useEffect(() => {
    if (!autoRefresh) return;
    
    const interval = setInterval(() => {
      fetchHealthData();
    }, 30000);
    
    return () => clearInterval(interval);
  }, [autoRefresh, fetchHealthData]);

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  const getUptimeColor = (uptime) => {
    if (uptime >= 99) return "text-green-600";
    if (uptime >= 95) return "text-yellow-600";
    return "text-red-600";
  };

  const getLatencyColor = (latency) => {
    if (!latency) return "text-muted-foreground";
    if (latency < 100) return "text-green-600";
    if (latency < 300) return "text-yellow-600";
    return "text-red-600";
  };

  // Get unique DSP names for chart legend
  const dspNames = healthData?.dsps?.map(d => d.name).filter(Boolean) || [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="dsp-health-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Activity className="h-8 w-8 text-accent" />
            DSP Health Monitor
          </h1>
          <p className="text-muted-foreground">
            Real-time connectivity and latency metrics for DSP endpoints
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
            <SelectTrigger className="w-32">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1">Last 1 hour</SelectItem>
              <SelectItem value="6">Last 6 hours</SelectItem>
              <SelectItem value="24">Last 24 hours</SelectItem>
              <SelectItem value="72">Last 3 days</SelectItem>
              <SelectItem value="168">Last 7 days</SelectItem>
            </SelectContent>
          </Select>
          <Button
            variant={autoRefresh ? "default" : "outline"}
            size="sm"
            onClick={() => setAutoRefresh(!autoRefresh)}
            data-testid="auto-refresh-btn"
          >
            <Zap className={`h-4 w-4 mr-1 ${autoRefresh ? "animate-pulse" : ""}`} />
            {autoRefresh ? "Auto" : "Manual"}
          </Button>
          <Button
            variant="outline"
            onClick={() => fetchHealthData(true)}
            disabled={refreshing}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total DSPs</CardTitle>
            <Server className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{healthData?.summary?.total || 0}</div>
            <p className="text-xs text-muted-foreground">Configured endpoints</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Healthy</CardTitle>
            <CheckCircle className="h-4 w-4 text-green-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">{healthData?.summary?.healthy || 0}</div>
            <Progress 
              value={healthData?.summary?.total ? (healthData.summary.healthy / healthData.summary.total * 100) : 0} 
              className="mt-2 h-2"
            />
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Degraded</CardTitle>
            <AlertTriangle className="h-4 w-4 text-yellow-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-yellow-600">{healthData?.summary?.degraded || 0}</div>
            <p className="text-xs text-muted-foreground">Need attention</p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Unhealthy</CardTitle>
            <XCircle className="h-4 w-4 text-red-500" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">{healthData?.summary?.unhealthy || 0}</div>
            <p className="text-xs text-muted-foreground">Connection issues</p>
          </CardContent>
        </Card>
      </div>

      {/* Latency Chart */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5" />
            Latency Over Time
          </CardTitle>
          <CardDescription>
            Average response latency (ms) for DSP endpoints
          </CardDescription>
        </CardHeader>
        <CardContent>
          {latencyData.length > 0 ? (
            <div className="h-64">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={latencyData}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                  />
                  <YAxis 
                    tick={{ fontSize: 12 }}
                    className="text-muted-foreground"
                    unit="ms"
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: 'hsl(var(--card))', 
                      border: '1px solid hsl(var(--border))',
                      borderRadius: '8px'
                    }}
                  />
                  <Legend />
                  <Line 
                    type="monotone" 
                    dataKey="avgLatency" 
                    name="Average"
                    stroke="hsl(var(--primary))" 
                    strokeWidth={2}
                    dot={false}
                  />
                  {dspNames.slice(0, 5).map((name, i) => (
                    <Line
                      key={name}
                      type="monotone"
                      dataKey={name}
                      name={name}
                      stroke={`hsl(${(i * 60) % 360}, 70%, 50%)`}
                      strokeWidth={1}
                      strokeDasharray="3 3"
                      dot={false}
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-64 flex items-center justify-center text-muted-foreground">
              No latency data available. Run health checks to collect data.
            </div>
          )}
        </CardContent>
      </Card>

      {/* DSP Health Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wifi className="h-5 w-5" />
            DSP Endpoint Status
          </CardTitle>
          <CardDescription>
            Current health status and metrics for all configured DSP endpoints
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>DSP Name</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Endpoint URL</TableHead>
                <TableHead className="text-right">Latency</TableHead>
                <TableHead className="text-right">Uptime</TableHead>
                <TableHead className="text-right">Last Check</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {healthData?.dsps?.map((dsp) => {
                // Find historical metrics for this DSP
                const metrics = historyData?.dsp_metrics?.find(m => m.dsp_id === dsp.dsp_id) || {};
                
                return (
                  <TableRow key={dsp.dsp_id} data-testid={`dsp-row-${dsp.dsp_id}`}>
                    <TableCell className="font-medium">{dsp.name}</TableCell>
                    <TableCell>{getStatusBadge(dsp.status)}</TableCell>
                    <TableCell className="max-w-xs truncate text-xs text-muted-foreground font-mono">
                      {dsp.endpoint_url || "Not configured"}
                    </TableCell>
                    <TableCell className={`text-right font-mono ${getLatencyColor(dsp.latency_ms)}`}>
                      {dsp.latency_ms ? `${dsp.latency_ms}ms` : "-"}
                    </TableCell>
                    <TableCell className={`text-right font-medium ${getUptimeColor(metrics.uptime_percent || 0)}`}>
                      {metrics.uptime_percent !== undefined ? `${metrics.uptime_percent}%` : "-"}
                    </TableCell>
                    <TableCell className="text-right text-xs text-muted-foreground">
                      {dsp.last_check ? new Date(dsp.last_check).toLocaleTimeString() : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
              {(!healthData?.dsps || healthData.dsps.length === 0) && (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                    No DSP endpoints configured
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* DSP Bid Rate Monitor */}
      {bidRateData && (
        <Card data-testid="dsp-bid-rate-card">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5" />
              DSP Bid Rate Monitor
            </CardTitle>
            <CardDescription>
              Live forwarding metrics — requests sent to DSP endpoints, bids received, and circuit breaker status
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Global Summary */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
              <div className="p-3 rounded-lg bg-muted/30 text-center">
                <p className="text-xs text-muted-foreground">Requests Forwarded</p>
                <p className="text-xl font-bold text-blue-600">{bidRateData.global?.total_live_requests || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 text-center">
                <p className="text-xs text-muted-foreground">With Bids</p>
                <p className="text-xl font-bold text-emerald-600">{bidRateData.global?.total_with_bid || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 text-center">
                <p className="text-xs text-muted-foreground">Total Bids</p>
                <p className="text-xl font-bold text-violet-600">{bidRateData.global?.total_bids || 0}</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 text-center">
                <p className="text-xs text-muted-foreground">Bid Rate</p>
                <p className="text-xl font-bold">{bidRateData.global?.overall_bid_rate || 0}%</p>
              </div>
              <div className="p-3 rounded-lg bg-muted/30 text-center">
                <p className="text-xs text-muted-foreground">Avg Latency</p>
                <p className="text-xl font-bold text-amber-600">{bidRateData.global?.avg_latency_ms || 0}ms</p>
              </div>
            </div>

            {/* Per-DSP Table */}
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>DSP Name</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead className="text-right">Requests Sent</TableHead>
                  <TableHead className="text-right">Successful</TableHead>
                  <TableHead className="text-right">Failed</TableHead>
                  <TableHead className="text-right">Bid Rate</TableHead>
                  <TableHead className="text-right">Success Rate</TableHead>
                  <TableHead className="text-center">Circuit</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {bidRateData.dsps?.map((dsp) => (
                  <TableRow key={dsp.dsp_id} data-testid={`bid-rate-row-${dsp.dsp_id.slice(0, 8)}`}>
                    <TableCell className="font-medium">{dsp.name}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-xs text-muted-foreground font-mono">
                      {dsp.endpoint_url || "—"}
                    </TableCell>
                    <TableCell className="text-right font-mono">{dsp.requests_sent}</TableCell>
                    <TableCell className="text-right font-mono text-emerald-600">{dsp.successful_calls}</TableCell>
                    <TableCell className="text-right font-mono text-red-600">{dsp.failed_calls}</TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono font-medium ${
                        dsp.bid_rate >= 50 ? "text-emerald-600" : dsp.bid_rate >= 20 ? "text-amber-600" : "text-red-600"
                      }`}>
                        {dsp.bid_rate}%
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <span className={`font-mono text-xs px-1.5 py-0.5 rounded ${
                        dsp.success_rate >= 95 ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400" :
                        dsp.success_rate >= 80 ? "bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400" :
                        "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                      }`}>
                        {dsp.success_rate}%
                      </span>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge variant="outline" className={
                        dsp.circuit_state === "closed" ? "border-emerald-500 text-emerald-600" :
                        dsp.circuit_state === "open" ? "border-red-500 text-red-600" :
                        dsp.circuit_state === "half_open" ? "border-amber-500 text-amber-600" :
                        "border-gray-400 text-gray-500"
                      }>
                        {(dsp.circuit_state || "none").toUpperCase()}
                      </Badge>
                    </TableCell>
                  </TableRow>
                ))}
                {(!bidRateData.dsps || bidRateData.dsps.length === 0) && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-6 text-muted-foreground">
                      No active DSPs. Activate DSPs and send traffic to see bid rate metrics.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Historical Metrics */}
      {historyData?.dsp_metrics && historyData.dsp_metrics.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" />
              Historical Metrics
            </CardTitle>
            <CardDescription>
              Performance statistics over the selected time period ({historyData.period_hours} hours)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {historyData.dsp_metrics.map((metrics) => (
                <Card key={metrics.dsp_id} className="border-l-4" style={{
                  borderLeftColor: metrics.uptime_percent >= 99 ? 'hsl(var(--chart-1))' : 
                                   metrics.uptime_percent >= 95 ? 'hsl(var(--chart-3))' : 'hsl(var(--destructive))'
                }}>
                  <CardContent className="pt-4">
                    <div className="flex justify-between items-start mb-2">
                      <h4 className="font-semibold truncate">{metrics.name}</h4>
                      <Badge variant="outline" className={getUptimeColor(metrics.uptime_percent)}>
                        {metrics.uptime_percent}% uptime
                      </Badge>
                    </div>
                    <div className="grid grid-cols-3 gap-2 text-sm">
                      <div>
                        <p className="text-muted-foreground">Avg Latency</p>
                        <p className={`font-mono font-medium ${getLatencyColor(metrics.avg_latency)}`}>
                          {metrics.avg_latency || 0}ms
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Min</p>
                        <p className="font-mono font-medium text-green-600">
                          {metrics.min_latency || 0}ms
                        </p>
                      </div>
                      <div>
                        <p className="text-muted-foreground">Max</p>
                        <p className={`font-mono font-medium ${getLatencyColor(metrics.max_latency)}`}>
                          {metrics.max_latency || 0}ms
                        </p>
                      </div>
                    </div>
                    <p className="text-xs text-muted-foreground mt-2">
                      {metrics.checks} health checks performed
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Last Updated */}
      <p className="text-xs text-center text-muted-foreground">
        Last updated: {healthData?.checked_at ? new Date(healthData.checked_at).toLocaleString() : "Never"}
        {autoRefresh && " • Auto-refresh enabled (30s)"}
      </p>
    </div>
  );
}
